import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMenu } from '../../contexts/MenuContext';
import { useLocation } from '../../contexts/LocationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCopy, FiMapPin } = FiIcons;

const MenuCopyModal = ({ selectedItems, onClose }) => {
  const { menuItems, copyMenuItems } = useMenu();
  const { locations } = useLocation();
  const [targetLocations, setTargetLocations] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const selectedMenuItems = menuItems.filter(item => selectedItems.includes(item.id));

  const handleLocationToggle = (locationId) => {
    setTargetLocations(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleCopy = async () => {
    if (targetLocations.length === 0) {
      alert('Please select at least one target location');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await copyMenuItems(selectedItems, targetLocations);
      if (result.success) {
        alert(`Successfully copied ${selectedItems.length} items to ${targetLocations.length} location(s)`);
        onClose();
      } else {
        alert(result.error);
      }
    } catch (error) {
      alert('Failed to copy menu items');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-warm-brown/10 p-6 flex items-center justify-between">
            <h2 className="text-2xl font-playfair text-warm-brown">
              Copy Menu Items
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} className="text-warm-brown text-xl" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Selected Items Preview */}
            <div>
              <h3 className="text-lg font-playfair text-warm-brown mb-3">
                Selected Items ({selectedItems.length})
              </h3>
              <div className="bg-cream rounded-lg p-4 max-h-48 overflow-y-auto">
                <div className="space-y-2">
                  {selectedMenuItems.map(item => (
                    <div key={item.id} className="flex items-center justify-between bg-white rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        <div>
                          <h4 className="font-medium text-warm-brown">{item.name}</h4>
                          <p className="text-sm text-warm-brown/70">${parseFloat(item.price).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Target Locations */}
            <div>
              <h3 className="text-lg font-playfair text-warm-brown mb-3">
                Select Target Locations
              </h3>
              <div className="space-y-3">
                {locations.map(location => (
                  <label
                    key={location.id}
                    className="flex items-center gap-3 p-3 bg-cream rounded-lg hover:bg-warm-brown/5 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={targetLocations.includes(location.id)}
                      onChange={() => handleLocationToggle(location.id)}
                      className="w-5 h-5 text-warm-brown border-warm-brown/20 rounded focus:ring-warm-brown/50"
                    />
                    <SafeIcon icon={FiMapPin} className="text-warm-brown" />
                    <div className="flex-1">
                      <h4 className="font-medium text-warm-brown">{location.name}</h4>
                      <p className="text-sm text-warm-brown/70">{location.address}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Copy Options */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Copy Information</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Items will be copied with all their properties (price, description, etc.)</li>
                <li>• Customization options and ingredients will be preserved</li>
                <li>• Items will be added to the existing menu at target locations</li>
                <li>• Duplicates will be skipped if items already exist</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleCopy}
                disabled={isProcessing || targetLocations.length === 0}
                className="flex-1 bg-warm-brown text-cream py-3 rounded-lg font-medium hover:bg-warm-brown/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SafeIcon icon={FiCopy} className="text-sm" />
                {isProcessing ? 'Copying...' : `Copy to ${targetLocations.length} Location(s)`}
              </button>
              <button
                onClick={onClose}
                className="flex-1 border border-warm-brown/20 text-warm-brown py-3 rounded-lg font-medium hover:bg-warm-brown/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MenuCopyModal;